import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { GridApiPro } from "@mui/x-data-grid-pro";

import {
  FOOTER_HEIGHT_PX,
  PINNED_RIGHT_SHIFT_VAR,
} from "features/ui/MaterialTable/constants";

/**
 * Provides JavaScript-driven horizontal scrolling for a container whose
 * CSS overflow is set to `clip` (which disables native scroll but does not
 * create a scroll context — required to keep `position: sticky` working
 * relative to the page-level scroll ancestor).
 *
 * Returns:
 * - `containerRef` — attach to the wrapper around the content to clip
 * - `scrollbarRef` — attach to the thin scrollbar element rendered below
 * - `scrollbarProps` — spread onto the scrollbar element (onScroll handler)
 * - `showScrollbar` — whether the content overflows and the scrollbar is needed
 * - `totalWidth` — total content width (for the scrollbar spacer)
 */
export const useHorizontalOverflowScroll = (
  enabled: boolean,
  apiRef: RefObject<GridApiPro | null>
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const scrollbarRef = useRef<HTMLDivElement | null>(null);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [totalWidth, setTotalWidth] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isHeaderFullyVisible, setIsHeaderFullyVisible] = useState(false);
  // Read inside `applyTransform`, which runs on every scroll frame, so it is a
  // ref rather than state.
  const maxScrollRef = useRef(0);

  // Selectors for the internal elements that need to be shifted. Both the
  // column-header area and the row area must move in lockstep.
  //
  // NOTE: do NOT include `.MuiDataGrid-virtualScrollerContent` here. The other
  // targets are descendants of it, so translating it as well makes their
  // transforms compound (-2X instead of -X), leaving whitespace on the right
  // that grows linearly with scroll distance.
  const SCROLL_TARGETS = [
    ".MuiDataGrid-topContainer",
    ".MuiDataGrid-virtualScrollerRenderZone",
    ".MuiDataGrid-bottomContainer",
    ".MuiDataGrid-filler",
  ].join(", ");

  const applyTransform = useCallback(
    (scrollLeft: number) => {
      const container = containerRef.current;
      if (!container) return;

      container.querySelectorAll<HTMLElement>(SCROLL_TARGETS).forEach((el) => {
        // make sure to not introduce extra whitespace when the horizontal scroll bar appears
        if (el.classList.contains("MuiDataGrid-virtualScrollerRenderZone")) {
          const match = el.style.transform.match(
            /translate3d\([^,]*,\s*([^,)]+),/
          );
          const translateY = match ? match[1].trim() : "0px";
          el.style.transform = `translate3d(-${scrollLeft}px, ${translateY}, 0)`;
        } else {
          el.style.transform = `translateX(-${scrollLeft}px)`;
        }
      });

      // Right-pinned cells ride along with the translated containers above.
      container.style.setProperty(
        PINNED_RIGHT_SHIFT_VAR,
        `${scrollLeft - maxScrollRef.current}px`
      );
    },
    [SCROLL_TARGETS]
  );

  // Scrollbar → content sync
  const handleScrollbarScroll = useCallback(() => {
    if (scrollbarRef.current) {
      applyTransform(scrollbarRef.current.scrollLeft);
    }
  }, [applyTransform]);

  // Wheel/trackpad horizontal scroll on the grid → scrollbar sync
  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      // Only intercept predominantly-horizontal gestures, plus shift+wheel —
      // the mouse equivalent of a sideways swipe.
      if (!e.shiftKey && Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;

      if (!scrollbarRef.current) return;

      // Browsers disagree on which axis carries shift+wheel: macOS swaps it
      // onto deltaX, Windows and Linux leave it on deltaY.
      const deltaX = e.shiftKey && e.deltaX === 0 ? e.deltaY : e.deltaX;

      scrollbarRef.current.scrollLeft += deltaX;
      e.preventDefault();
    };

    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => container.removeEventListener("wheel", handleWheel);
  }, [enabled]);

  // Track the grid's total content width. This is the grid's own measure of
  // how wide the columns are, so it stays correct when a column is resized —
  // unlike reading the DOM, where the content element is a flex box whose
  // measured width does not reflect resizes.
  useEffect(() => {
    if (!enabled) return;

    const api = apiRef.current;
    if (!api) return;

    const syncTotalWidth = () =>
      setTotalWidth(api.getRootDimensions().columnsTotalWidth);

    syncTotalWidth();

    // `debouncedResize` is published whenever the grid recomputes its
    // dimensions — on viewport resize and, crucially, on column resize — and
    // fires after `columnsTotalWidth` is up to date.
    return api.subscribeEvent("debouncedResize", syncTotalWidth);
  }, [enabled, apiRef]);

  // Decide whether the scrollbar is needed by comparing the content width to
  // the visible width, and keep the footer offset in sync. Re-runs whenever
  // the content width changes or the container itself is resized.
  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      setShowScrollbar(totalWidth > container.clientWidth);
      maxScrollRef.current = Math.max(0, totalWidth - container.clientWidth);
      applyTransform(scrollbarRef.current?.scrollLeft ?? 0);

      const footer = container.querySelector<HTMLElement>(
        ".MuiDataGrid-footerContainer"
      );
      setFooterHeight(footer?.offsetHeight ?? 0);
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);

    return () => observer.disconnect();
  }, [enabled, totalWidth, applyTransform]);

  // Check whether the grid has enough visible height for the footer to be
  // sticky without overlapping the header. Observes the container div (which
  // is NOT affected by the horizontal transform applied to SCROLL_TARGETS)
  // so horizontal scrolling doesn't interfere with the check.
  useEffect(() => {
    if (!enabled) return;

    const container = containerRef.current;
    if (!container) return;

    const topContainer = container.querySelector<HTMLElement>(
      ".MuiDataGrid-topContainer"
    );
    const headerH = topContainer?.offsetHeight ?? 0;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        // The visible height of the container must fit the header + footer
        // with some breathing room for the scrollbar.
        setIsHeaderFullyVisible(
          entry.intersectionRect.height > headerH + FOOTER_HEIGHT_PX
        );
      },
      { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
    );
    observer.observe(container);

    return () => observer.disconnect();
  }, [enabled]);

  return {
    containerRef,
    scrollbarRef,
    scrollbarProps: { onScroll: handleScrollbarScroll },
    showScrollbar: enabled && showScrollbar,
    isHeaderFullyVisible,
    totalWidth,
    footerHeight,
  };
};
